import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import clsx from 'clsx';
import { AlertTriangle, Ban, CheckCircle2, Clock, Loader2, Lock, Paperclip, Upload, X } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════════════════════
// PUBLIC online referral form (SC Workflow Specification v1.0, 6.7), at /forms/referral/:token.
// No login and no app chrome: the emailed link's token is the credential. Plain axios on purpose,
// because the app's api client sends people to /login on a 401. The company's name and logo come
// from the API; violet is the accent so the page reads as the same product as the emails.
// ═══════════════════════════════════════════════════════════════════════════════════════════════

const http = axios.create({ baseURL: '/api/sc-forms', timeout: 120000 });
const MAX_FILES = 10;
const MAX_FILE = 15 * 1024 * 1024;
const ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.rtf,.png,.jpg,.jpeg,.gif,.webp,.heic';
const INPUT = 'w-full rounded-xl border-0 bg-white ring-1 ring-inset ring-slate-300 px-3.5 text-base text-slate-900 placeholder:text-slate-500 shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-violet-600 aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-rose-500 disabled:opacity-60';

const isEmail = (s) => { const v = String(s || '').trim(); return v.length <= 255 && /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(v); };
const isPhone = (s) => { const v = String(s || '').trim(); return v.length <= 30 && /^[0-9+() .-]{6,30}$/.test(v) && v.replace(/\D/g, '').length >= 6; };
const isNdis = (s) => /^\d{9}$/.test(String(s || '').replace(/[\s-]/g, ''));
function validDate(s) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(s || ''))) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
const auDate = (s) => {
  if (!s) return '';
  const [y, m, d] = String(s).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(s);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
};
const fileSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fieldId = (key) => `q-${key}`;
const isRequired = (f, values) => !!(f.required || (f.required_if && values[f.required_if.key] === f.required_if.equals));
// The respondent's own contact details can be autofilled; the participant's never are.
const AUTOCOMPLETE = {
  referrer_name: 'name', completed_by_name: 'name', contact_person: 'name', referrer_email: 'email', email: 'email',
  referrer_phone: 'tel', phone: 'tel', referrer_org: 'organization', organisation: 'organization', referrer_role: 'organization-title',
};

function validate(fields, values, files) {
  const errs = {};
  fields.forEach((f) => {
    const req = isRequired(f, values);
    if (f.type === 'file') {
      if (req && !(files[f.key] || []).length) errs[f.key] = `Attach a file for ${f.label}.`;
      return;
    }
    if (f.type === 'checkbox') {
      if (req && values[f.key] !== true) errs[f.key] = `Tick ${f.label}.`;
      return;
    }
    const s = String(values[f.key] == null ? '' : values[f.key]).trim();
    if (!s) { if (req) errs[f.key] = `${f.label} is required.`; return; }
    if (f.type === 'text' && s.length > 500) errs[f.key] = `${f.label} is too long (500 characters at most).`;
    else if (f.type === 'textarea' && s.length > 5000) errs[f.key] = `${f.label} is too long (5,000 characters at most).`;
    else if (f.type === 'email' && !isEmail(s)) errs[f.key] = `${f.label}: enter an email address like name@example.com.`;
    else if (f.type === 'phone' && !isPhone(s)) errs[f.key] = `${f.label}: enter a phone number with at least 6 digits.`;
    else if (f.type === 'date' && !validDate(s)) errs[f.key] = `${f.label}: choose a date.`;
    else if (f.type === 'select' && !(f.options || []).some((o) => o.value === s)) errs[f.key] = `${f.label}: choose one of the options.`;
    else if (f.key === 'ndis_number' && !isNdis(s)) errs[f.key] = 'NDIS number: enter the 9 digits.';
  });
  return errs;
}

export default function ScReferralForm() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading');
  const [data, setData] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [done, setDone] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setPhase('loading');
    http.get(`/${encodeURIComponent(token || '')}`)
      .then((r) => { if (!live) return; setData(r.data); setPhase('form'); })
      .catch((e) => {
        if (!live) return;
        const res = e.response;
        if (res && (res.status === 404 || res.status === 410)) {
          setBlocked({ state: res.data?.state || 'not_found', message: res.data?.message, company: res.data?.company || null });
          setPhase('blocked');
        } else {
          setLoadError(res?.data?.message || 'We couldn’t load this form. Check your internet connection and try again.');
          setPhase('error');
        }
      });
    return () => { live = false; };
  }, [token, attempt]);

  const company = data?.company || blocked?.company || done?.company || null;
  useEffect(() => {
    const name = company?.name;
    if (phase === 'form' && data) document.title = `${data.formName || 'Referral'} form${name ? ` · ${name}` : ''}`;
    else if (phase === 'done') document.title = `Thank you${name ? ` · ${name}` : ''}`;
    else document.title = name ? `${name} · Referral form` : 'Referral form';
  }, [phase, data, company]);

  return (
    <Shell company={company}>
      {phase === 'loading' && <LoadingCard />}
      {phase === 'error' && (
        <MessageCard icon={AlertTriangle} tone="rose" title="Something went wrong">
          <p>{loadError}</p>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}
            className="mt-5 inline-flex items-center justify-center h-11 px-5 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2">
            Try again
          </button>
        </MessageCard>
      )}
      {phase === 'blocked' && <BlockedCard state={blocked?.state} company={company} />}
      {phase === 'form' && data && (
        <FormCard token={token} data={data}
          onDone={(msg) => { setDone({ message: msg, company: data.company }); setPhase('done'); window.scrollTo(0, 0); }}
          onBlocked={(b) => { setBlocked(b); setPhase('blocked'); window.scrollTo(0, 0); }} />
      )}
      {phase === 'done' && (
        <MessageCard icon={CheckCircle2} tone="emerald" title="Thank you, your answers have been sent" focus>
          <p>{done?.message || `Your answers have been sent to ${company?.name || 'the team'}.`}</p>
          <p className="mt-3">They will be in touch if they need anything else. You can close this page.</p>
        </MessageCard>
      )}
    </Shell>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────────────────────
function Shell({ company, children }) {
  const brand = /^#[0-9a-f]{3,8}$/i.test(String(company?.brandColor || '')) ? company.brandColor : null;
  const name = company?.name || '';
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 antialiased">
      <div className={clsx('h-1.5 w-full', !brand && 'bg-violet-600')} style={brand ? { backgroundColor: brand } : undefined} aria-hidden />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {company?.logoDataUrl ? (
              <img src={company.logoDataUrl} alt={name ? `${name} logo` : 'Company logo'} className="h-10 w-auto max-w-[160px] object-contain" />
            ) : name ? (
              <span className="w-10 h-10 rounded-xl bg-violet-100 text-violet-700 flex items-center justify-center font-bold" aria-hidden>{name.slice(0, 1).toUpperCase()}</span>
            ) : null}
            {name && <span className="text-[15px] font-semibold text-slate-900 truncate">{name}</span>}
          </div>
          <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-slate-500 flex-shrink-0">
            <Lock size={13} aria-hidden /> Secure form
          </span>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
      <footer className="max-w-2xl mx-auto px-4 sm:px-6 pb-10 text-center text-[12.5px] text-slate-500">
        <p>{name ? `Your answers go only to ${name}.` : 'Your answers go only to the organisation that sent you this link.'} No account or login is needed.</p>
        <p className="mt-1">Powered by Rostery</p>
      </footer>
    </div>
  );
}

function LoadingCard() {
  return (
    <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-6 sm:p-8" aria-busy="true" aria-live="polite">
      <p className="sr-only">Loading the form…</p>
      <div className="animate-pulse space-y-4" aria-hidden>
        <div className="h-3 w-24 rounded bg-violet-100" />
        <div className="h-7 w-2/3 rounded bg-slate-100" />
        <div className="h-4 w-full rounded bg-slate-100" />
        <div className="h-12 w-full rounded-xl bg-slate-100" />
        <div className="h-12 w-full rounded-xl bg-slate-100" />
        <div className="h-12 w-full rounded-xl bg-slate-100" />
      </div>
    </div>
  );
}

const TONES = {
  emerald: 'bg-emerald-50 text-emerald-600', amber: 'bg-amber-50 text-amber-600', rose: 'bg-rose-50 text-rose-600', slate: 'bg-slate-100 text-slate-500', violet: 'bg-violet-50 text-violet-600',
};
function MessageCard({ icon: Icon, tone = 'violet', title, children, focus }) {
  const ref = useRef(null);
  useEffect(() => { if (focus && ref.current) ref.current.focus(); }, [focus]);
  return (
    <section className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm px-6 py-10 sm:px-10 text-center" aria-labelledby="msg-title">
      <span className={clsx('mx-auto w-14 h-14 rounded-2xl flex items-center justify-center', TONES[tone])}><Icon size={26} aria-hidden /></span>
      <h1 id="msg-title" ref={ref} tabIndex={-1} className="mt-5 text-[22px] sm:text-[24px] font-semibold tracking-[-0.01em] text-slate-900 outline-none">{title}</h1>
      <div className="mt-3 text-[15px] leading-relaxed text-slate-600 max-w-md mx-auto">{children}</div>
    </section>
  );
}

function BlockedCard({ state, company }) {
  const who = company?.name || 'the organisation that sent it';
  if (state === 'submitted') {
    return (
      <MessageCard icon={CheckCircle2} tone="emerald" title="This form has already been submitted">
        <p>Thank you. {company?.name || 'The team'} already has your answers.</p>
        <p className="mt-3">If something needs to change, please contact {who} directly.</p>
      </MessageCard>
    );
  }
  if (state === 'expired') {
    return (
      <MessageCard icon={Clock} tone="amber" title="This link has expired">
        <p>To keep information safe, form links only work for a limited time.</p>
        <p className="mt-3">Please contact {who} and ask them to send you a new link.</p>
      </MessageCard>
    );
  }
  if (state === 'cancelled') {
    return (
      <MessageCard icon={Ban} tone="slate" title="This link is no longer active">
        <p>The link has been replaced or cancelled, or the referral has been closed.</p>
        <p className="mt-3">If you still need to respond, please contact {who}. If you were sent a newer email, use the link in that one.</p>
      </MessageCard>
    );
  }
  return (
    <MessageCard icon={AlertTriangle} tone="rose" title="We can’t find this form">
      <p>The link may be incomplete. Please open it again from your email, or copy the whole link into your browser.</p>
      <p className="mt-3">If it still doesn’t work, contact the person who sent it to you.</p>
    </MessageCard>
  );
}

// ── The form ──────────────────────────────────────────────────────────────────────────────────
function FormCard({ token, data, onDone, onBlocked }) {
  const fields = useMemo(() => (Array.isArray(data.fields) ? data.fields : []), [data.fields]);
  const ctx = data.context || {};
  const isResponse = data.formType === 'referral_response';
  const [values, setValues] = useState(() => {
    const v = {};
    fields.forEach((f) => {
      const pre = ctx.prefill ? ctx.prefill[f.key] : undefined;
      if (f.type === 'checkbox') v[f.key] = ['yes', 'true', '1'].includes(String(pre || '').toLowerCase());
      else if (f.type !== 'file') v[f.key] = pre != null ? String(pre) : '';
    });
    return v;
  });
  const [files, setFiles] = useState({});
  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState('');
  const [sending, setSending] = useState(false);
  const summaryRef = useRef(null);
  const errorKeys = Object.keys(errors).filter((k) => errors[k]);
  const fileCount = Object.values(files).reduce((t, l) => t + l.length, 0);

  const setValue = (key, val) => {
    setValues((p) => ({ ...p, [key]: val }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };
  const addFiles = (key, list) => {
    const picked = Array.from(list || []);
    const tooBig = picked.filter((f) => f.size > MAX_FILE);
    let ok = picked.filter((f) => f.size <= MAX_FILE);
    const room = MAX_FILES - fileCount;
    let msg = '';
    if (tooBig.length) msg = `${tooBig.map((f) => f.name).join(', ')} ${tooBig.length === 1 ? 'is' : 'are'} over 15 MB, so ${tooBig.length === 1 ? 'it was' : 'they were'} not added.`;
    if (ok.length > room) { ok = ok.slice(0, Math.max(0, room)); msg = `${msg ? `${msg} ` : ''}You can attach up to ${MAX_FILES} files in total.`; }
    if (ok.length) setFiles((p) => ({ ...p, [key]: [...(p[key] || []), ...ok] }));
    setErrors((x) => ({ ...x, [key]: msg || undefined }));
  };
  const removeFile = (key, idx) => setFiles((p) => ({ ...p, [key]: (p[key] || []).filter((_, i) => i !== idx) }));

  const focusField = (key) => {
    const el = document.getElementById(fieldId(key));
    if (el) { el.focus(); el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
  };
  const showErrors = (errs) => {
    setErrors(errs);
    setTimeout(() => { if (summaryRef.current) summaryRef.current.focus(); }, 30);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setTopError('');
    const errs = validate(fields, values, files);
    if (Object.keys(errs).length) { showErrors(errs); return; }
    setErrors({});
    const answers = {};
    fields.forEach((f) => {
      if (f.type === 'file') return;
      answers[f.key] = f.type === 'checkbox' ? values[f.key] === true : String(values[f.key] == null ? '' : values[f.key]).trim();
    });
    const all = [];
    Object.entries(files).forEach(([key, list]) => list.forEach((file) => all.push([key, file])));
    setSending(true);
    try {
      let res;
      if (all.length) {
        const fd = new FormData();
        fd.append('answers', JSON.stringify(answers));
        all.forEach(([key, file]) => fd.append(key, file, file.name));
        res = await http.post(`/${encodeURIComponent(token)}`, fd);
      } else {
        res = await http.post(`/${encodeURIComponent(token)}`, { answers });
      }
      onDone(res.data?.message);
    } catch (err) {
      const res = err.response;
      if (res && (res.status === 404 || res.status === 410)) {
        onBlocked({ state: res.data?.state || 'not_found', message: res.data?.message, company: res.data?.company || data.company || null });
        return;
      }
      if (res && res.status === 400 && Array.isArray(res.data?.errors) && res.data.errors.length) {
        const map = {};
        res.data.errors.forEach((x) => { if (x && x.key) map[x.key] = x.message; });
        if (Object.keys(map).some((k) => fields.some((f) => f.key === k))) { showErrors(map); return; }
      }
      setTopError(res?.data?.message || 'We couldn’t send your answers. Check your internet connection and try again. Nothing you typed has been lost.');
      setTimeout(() => { if (summaryRef.current) summaryRef.current.focus(); }, 30);
    } finally { setSending(false); }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-5" aria-labelledby="form-title">
      <section className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 sm:p-8">
        <p className="text-[12px] font-semibold uppercase tracking-[0.08em] text-violet-700">
          {ctx.referral_number ? `Referral ${ctx.referral_number}` : 'Referral'}
        </p>
        <h1 id="form-title" className="mt-1.5 text-[24px] sm:text-[28px] leading-tight font-semibold tracking-[-0.015em] text-slate-900">
          {data.formName || 'Referral'} form
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-slate-600">
          {isResponse
            ? `${data.company?.name || 'We'} would like to refer a participant to your organisation. Please tell us whether you can accept the referral.`
            : `${data.company?.name || 'We'} needs a few more details about this referral. We’ve filled in what we already have. Please check it and complete the rest.`}
        </p>
        {isResponse && (
          <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 rounded-xl bg-violet-50/70 ring-1 ring-inset ring-violet-100 p-4">
            {ctx.participant && <Fact label="Participant" value={ctx.participant} />}
            {ctx.coordinator && <Fact label="Support coordinator" value={ctx.coordinator} />}
            {ctx.service_requested && <Fact label="Service requested" value={ctx.service_requested} wide />}
            {ctx.reason && <Fact label="Reason for referral" value={ctx.reason} wide />}
          </dl>
        )}
        {ctx.expires_on && (
          <p className="mt-4 flex items-start gap-1.5 text-[13.5px] text-slate-600">
            <Clock size={14} className="mt-0.5 flex-shrink-0 text-violet-600" aria-hidden /><span>Please respond by {auDate(ctx.expires_on)}. The link stops working after that.</span>
          </p>
        )}
      </section>

      {(errorKeys.length > 0 || topError) && (
        <div ref={summaryRef} tabIndex={-1} role="alert" className="rounded-2xl bg-rose-50 ring-1 ring-inset ring-rose-200 p-4 sm:p-5 outline-none focus-visible:ring-2 focus-visible:ring-rose-500">
          <p className="flex items-center gap-2 text-[15px] font-semibold text-rose-800"><AlertTriangle size={17} aria-hidden />
            {topError ? 'Your answers weren’t sent' : `Please fix ${errorKeys.length === 1 ? 'this answer' : `these ${errorKeys.length} answers`}`}
          </p>
          {topError ? <p className="mt-1.5 text-[14px] text-rose-800">{topError}</p> : (
            <ul className="mt-2 space-y-1 text-[14px]">
              {errorKeys.map((k) => (
                <li key={k}>
                  <a href={`#${fieldId(k)}`} onClick={(e) => { e.preventDefault(); focusField(k); }} className="text-rose-800 underline underline-offset-2 hover:text-rose-900 break-words">{errors[k]}</a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <section className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5 sm:p-8" aria-label="Questions">
        <p className="text-[13.5px] text-slate-500 mb-6">Questions marked <span className="text-rose-600 font-semibold" aria-hidden>*</span><span className="sr-only">with a star</span> must be answered.</p>
        <div className="space-y-6">
          {fields.map((f) => (
            <Question key={f.key} f={f} value={values[f.key]} required={isRequired(f, values)} error={errors[f.key]} disabled={sending}
              onChange={(v) => setValue(f.key, v)} files={files[f.key] || []} fileRoom={MAX_FILES - fileCount}
              onAddFiles={(list) => addFiles(f.key, list)} onRemoveFile={(i) => removeFile(f.key, i)} />
          ))}
          {!fields.length && <p className="text-[15px] text-slate-600">This form has no questions. You can submit it to let the sender know you have seen it.</p>}
        </div>
      </section>

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-[13px] text-slate-500 text-center sm:text-left">Your answers are sent securely and can’t be changed after you submit.</p>
        <button type="submit" disabled={sending}
          className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-xl bg-violet-600 text-white text-[15px] font-semibold shadow-sm hover:bg-violet-700 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 w-full sm:w-auto">
          {sending && <Loader2 size={17} className="animate-spin" aria-hidden />}
          {sending ? 'Sending…' : 'Submit answers'}
        </button>
      </div>
    </form>
  );
}

function Fact({ label, value, wide }) {
  return (
    <div className={clsx('min-w-0', wide && 'sm:col-span-2')}>
      <dt className="text-[12.5px] font-medium text-violet-800/80">{label}</dt>
      <dd className="mt-0.5 text-[15px] text-slate-900 whitespace-pre-wrap break-words">{value}</dd>
    </div>
  );
}

function Label({ htmlFor, id, children, required, as: Tag = 'label' }) {
  return (
    <Tag htmlFor={Tag === 'label' ? htmlFor : undefined} id={id} className="block text-[15px] font-semibold text-slate-900">
      {children}
      {required && <><span className="text-rose-600 ml-0.5" aria-hidden>*</span><span className="sr-only"> (required)</span></>}
    </Tag>
  );
}

function Question({ f, value, required, error, disabled, onChange, files, fileRoom, onAddFiles, onRemoveFile }) {
  const id = fieldId(f.key);
  const helpId = f.help ? `${id}-help` : null;
  const errId = error ? `${id}-err` : null;
  const describedBy = [helpId, errId].filter(Boolean).join(' ') || undefined;
  const help = f.help ? <p id={helpId} className="mt-1 text-[13.5px] text-slate-500">{f.help}</p> : null;
  const err = error ? (
    <p id={errId} className="mt-1.5 flex items-start gap-1.5 text-[13.5px] font-medium text-rose-700"><AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden />{error}</p>
  ) : null;
  const common = { id, disabled, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined, 'aria-required': required || undefined };

  if (f.type === 'select' && (f.options || []).length <= 5) {
    return (
      <fieldset aria-describedby={describedBy} aria-invalid={error ? true : undefined}>
        <Label as="legend" required={required}>{f.label}</Label>
        {help}
        <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(f.options || []).map((o, i) => {
            const on = value === o.value;
            return (
              <label key={o.value}
                className={clsx('flex items-center gap-3 min-h-[48px] px-4 py-2.5 rounded-xl ring-1 ring-inset cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-violet-600',
                  on ? 'ring-violet-500 bg-violet-50 text-violet-900' : 'ring-slate-300 bg-white text-slate-800 hover:bg-slate-50', error && !on && 'ring-rose-300')}>
                <input type="radio" name={f.key} id={i === 0 ? id : undefined} value={o.value} checked={on} disabled={disabled}
                  onChange={() => onChange(o.value)} className="w-4 h-4 accent-violet-600 flex-shrink-0" />
                <span className="text-[15px] font-medium">{o.label}</span>
              </label>
            );
          })}
        </div>
        {err}
      </fieldset>
    );
  }
  if (f.type === 'select') {
    return (
      <div>
        <Label htmlFor={id} required={required}>{f.label}</Label>
        {help}
        <select {...common} value={value || ''} onChange={(e) => onChange(e.target.value)} className={clsx(INPUT, 'mt-2 h-12 pr-8')}>
          <option value="">Choose…</option>
          {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {err}
      </div>
    );
  }
  if (f.type === 'checkbox') {
    return (
      <div>
        <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" {...common} checked={value === true} onChange={(e) => onChange(e.target.checked)} className="mt-1 w-5 h-5 rounded accent-violet-600 flex-shrink-0" />
          <span className="text-[15px] font-semibold text-slate-900">
            {f.label}{required && <><span className="text-rose-600 ml-0.5" aria-hidden>*</span><span className="sr-only"> (required)</span></>}
          </span>
        </label>
        <div className="pl-8">{help}{err}</div>
      </div>
    );
  }
  if (f.type === 'file') {
    return (
      <div>
        <Label as="p" id={`${id}-lbl`} required={required}>{f.label}</Label>
        {help}
        <p id={`${id}-types`} className="sr-only">PDF, Word, Excel, CSV, text or image files, 15 MB each, up to {MAX_FILES} files.</p>
        <label htmlFor={id}
          className={clsx('mt-2 flex flex-col items-center justify-center gap-1.5 min-h-[96px] px-4 py-5 rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-violet-600 focus-within:ring-offset-2',
            error ? 'border-rose-300 bg-rose-50/40' : 'border-slate-300 bg-slate-50 hover:bg-violet-50/50 hover:border-violet-300', (disabled || fileRoom <= 0) && 'opacity-60 cursor-not-allowed')}>
          <Upload size={20} className="text-violet-600" aria-hidden />
          <span className="text-[15px] font-semibold text-violet-700">Choose files</span>
          <span className="text-[13px] text-slate-500" aria-hidden>PDF, Word, Excel, CSV, text or images · 15 MB each · up to {MAX_FILES} files</span>
          <input type="file" multiple accept={ACCEPT} {...common} disabled={disabled || fileRoom <= 0} className="sr-only"
            aria-labelledby={`${id}-lbl`} aria-describedby={[helpId, `${id}-types`, errId].filter(Boolean).join(' ')}
            onChange={(e) => { onAddFiles(e.target.files); e.target.value = ''; }} />
        </label>
        {files.length > 0 && (
          <ul className="mt-2.5 space-y-1.5" aria-label={`Files attached to ${f.label}`}>
            {files.map((file, i) => (
              <li key={`${file.name}-${i}`} className="flex items-center gap-3 rounded-xl bg-white ring-1 ring-inset ring-slate-200 px-3.5 py-2.5">
                <Paperclip size={15} className="text-slate-400 flex-shrink-0" aria-hidden />
                <span className="flex-1 min-w-0 text-[14px] text-slate-800 truncate">{file.name}</span>
                <span className="text-[12.5px] text-slate-500 tabular-nums flex-shrink-0">{fileSize(file.size)}</span>
                <button type="button" onClick={() => onRemoveFile(i)} disabled={disabled} aria-label={`Remove ${file.name}`}
                  className="-my-2 -mr-2 w-11 h-11 inline-flex items-center justify-center rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-600">
                  <X size={16} aria-hidden />
                </button>
              </li>
            ))}
          </ul>
        )}
        {err}
      </div>
    );
  }
  if (f.type === 'textarea') {
    return (
      <div>
        <Label htmlFor={id} required={required}>{f.label}</Label>
        {help}
        <textarea {...common} rows={4} maxLength={5000} value={value || ''} onChange={(e) => onChange(e.target.value)} className={clsx(INPUT, 'mt-2 py-3 leading-relaxed')} />
        {err}
      </div>
    );
  }
  const type = f.type === 'email' ? 'email' : f.type === 'phone' ? 'tel' : f.type === 'date' ? 'date' : 'text';
  return (
    <div>
      <Label htmlFor={id} required={required}>{f.label}</Label>
      {help}
      <input {...common} type={type} value={value || ''} onChange={(e) => onChange(e.target.value)}
        maxLength={f.type === 'text' ? 500 : undefined}
        inputMode={f.key === 'ndis_number' ? 'numeric' : undefined}
        autoComplete={AUTOCOMPLETE[f.key] || 'off'}
        className={clsx(INPUT, 'mt-2 h-12', type === 'date' && 'sm:max-w-[240px]')} />
      {err}
    </div>
  );
}
